import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Plus, Edit01, Trash01, X, AlertTriangle } from "@untitledui/icons";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useTranslation } from "react-i18next";
import {
    useConversationsInfinite,
    useUpdateConversation,
    useBulkDeleteConversations,
    useToggleStarConversation,
    useDeleteConversation,
    type ConversationResponse,
} from "@/hooks/api/use-conversations";
import { useChatAI } from "./chat-ai-context";
import { cx } from "@/utils/cx";

const formatTimestamp = (timestamp?: string | null): string => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
        return "Adesso";
    } else if (diffMins < 60) {
        return `${diffMins}m fa`;
    } else if (diffHours < 24) {
        return `${diffHours}h fa`;
    } else if (diffDays < 7) {
        return `${diffDays}g fa`;
    }
    return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
    });
};

// Star icon component (since @untitledui/icons doesn't have Star)
const StarIcon = ({ filled, className }: { filled: boolean; className?: string }) => {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill={filled ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.5"
            className={className}
        >
            <path d="M8 1l2 4 4 1-3 3 1 4-4-2-4 2 1-4-3-3 4-1z" />
        </svg>
    );
};

// Wrapper for Dropdown icon prop
const StarIconWrapper = ({ className }: { className?: string }) => (
    <StarIcon filled={false} className={className} />
);

const StarIconFilledWrapper = ({ className }: { className?: string }) => (
    <StarIcon filled={true} className={className} />
);

type ChatHistorySidebarProps = {
    isOpen: boolean;
    onClose: () => void;
};

interface ChatItemProps {
    conversation: ConversationResponse;
    isActive: boolean;
    isSelected: boolean;
    isSelectionMode: boolean;
    editingConversationId: string | null;
    editingTitle: string;
    onSelect: (id: string, selected: boolean) => void;
    onOpen: (id: string, title: string | null) => void;
    onRename: (id: string) => void;
    onSaveRename: (id: string, title: string) => void;
    onCancelRename: () => void;
    onSetEditingTitle: (title: string) => void;
    onToggleStar: (id: string, starred: boolean) => void;
    onDelete: (id: string) => void;
    onEnterSelectionMode: (id: string) => void;
}

const ChatItem = ({
    conversation,
    isActive,
    isSelected,
    isSelectionMode,
    editingConversationId,
    editingTitle,
    onSelect,
    onOpen,
    onRename,
    onSaveRename,
    onCancelRename,
    onSetEditingTitle,
    onToggleStar,
    onDelete,
    onEnterSelectionMode,
}: ChatItemProps) => {
    const { t } = useTranslation(["common"]);
    const isEditing = editingConversationId === conversation.conversation_id;
    const title = conversation.title || t("aiChat.untitledChat", { defaultValue: "Chat senza titolo" });
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            onSaveRename(conversation.conversation_id, editingTitle.trim() || "");
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancelRename();
        }
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't open chat if clicking on checkbox, menu, or input
        const target = e.target as HTMLElement;
        if (
            target.closest('input') ||
            target.closest('[role="button"]') ||
            target.closest('button') ||
            target.closest('[aria-label]')
        ) {
            return;
        }
        if (!isSelectionMode && !isEditing) {
            onOpen(conversation.conversation_id, conversation.title);
        }
    };

    return (
        <div
            onClick={handleCardClick}
            className={cx(
                "group relative flex items-center gap-3 w-full rounded-md border px-4 py-3 transition",
                "hover:bg-primary_hover",
                isActive && "border-brand bg-active",
                isSelected && isSelectionMode && "bg-primary_hover border-brand",
                !isSelectionMode && !isEditing && "cursor-pointer"
            )}
        >
            <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    isSelected={isSelected}
                    onChange={(selected) => {
                        if (!isSelectionMode && selected) {
                            // Enter selection mode when first checkbox is checked
                            onEnterSelectionMode(conversation.conversation_id);
                        } else {
                            onSelect(conversation.conversation_id, selected);
                        }
                    }}
                    aria-label={t("aiChat.select")}
                />
            </div>

            <div className="flex-1 min-w-0">
                {isEditing ? (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Input
                            ref={inputRef}
                            value={editingTitle}
                            onChange={(value) => onSetEditingTitle(value)}
                            onKeyDown={handleKeyDown}
                            onBlur={() => onSaveRename(conversation.conversation_id, editingTitle.trim() || "")}
                            className="text-sm"
                        />
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        {conversation.starred && (
                            <StarIcon filled={true} className="size-4 text-warning-400 shrink-0" />
                        )}
                        <div className="text-left flex-1 min-w-0">
                            <span className="text-sm font-medium text-primary truncate block">{title}</span>
                            <span className="text-xs text-tertiary mt-0.5 block">
                                {t("aiChat.messageCount", {
                                    defaultValue: "{{count}} messaggi",
                                    count: conversation.message_count,
                                })}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {!isSelectionMode && !isEditing && (
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-tertiary">
                        {formatTimestamp(conversation.last_message_at || conversation.started_at)}
                    </span>
                    <Dropdown.Root>
                        <Dropdown.DotsButton />
                        <Dropdown.Popover className="w-min">
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    id="star"
                                    icon={conversation.starred ? StarIconFilledWrapper : StarIconWrapper}
                                    onAction={() => onToggleStar(conversation.conversation_id, !conversation.starred)}
                                >
                                    {conversation.starred ? t("aiChat.unstar") : t("aiChat.star")}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    id="rename"
                                    icon={Edit01}
                                    onAction={() => onRename(conversation.conversation_id)}
                                >
                                    {t("aiChat.rename")}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    id="delete"
                                    icon={Trash01}
                                    onAction={() => {
                                        onDelete(conversation.conversation_id);
                                    }}
                                >
                                    {t("aiChat.delete")}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                </div>
            )}
        </div>
    );
};

/**
 * Sidedrawer per lo storico chat con apertura conversazioni dal backend.
 */
export const ChatHistorySidebar = ({ isOpen, onClose }: ChatHistorySidebarProps) => {
    const { t } = useTranslation(["common"]);
    const { createNewChat, openConversation, chatTabs } = useChatAI();
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
    const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState("");
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        refetch,
    } = useConversationsInfinite({ is_active: true, limit: 20 });

    const updateConversation = useUpdateConversation();
    const bulkDeleteConversations = useBulkDeleteConversations();
    const toggleStarConversation = useToggleStarConversation();
    const deleteConversation = useDeleteConversation();

    // Flatten conversations from all pages and sort: starred first, then by date DESC
    const conversations = useMemo(() => {
        const allConversations = data?.pages.flatMap((page) => page) ?? [];

        // Separate starred and non-starred conversations
        const starred = allConversations.filter((c) => c.starred);
        const nonStarred = allConversations.filter((c) => !c.starred);

        // Sort function: by last_message_at or started_at DESC
        const sortByDate = (a: ConversationResponse, b: ConversationResponse) => {
            const dateA = a.last_message_at || a.started_at;
            const dateB = b.last_message_at || b.started_at;
            return new Date(dateB).getTime() - new Date(dateA).getTime();
        };

        // Sort both groups by date DESC
        starred.sort(sortByDate);
        nonStarred.sort(sortByDate);

        // Return starred first, then non-starred
        return [...starred, ...nonStarred];
    }, [data?.pages]);

    // Handle scroll for lazy loading
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || !hasNextPage || isFetchingNextPage) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // Load more when within 200px of bottom
        if (distanceFromBottom < 200) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, [handleScroll]);

    const handleSelect = (id: string, selected: boolean) => {
        setSelectedConversations((prev) => {
            const next = new Set(prev);
            if (selected) {
                next.add(id);
            } else {
                next.delete(id);
            }
            // Exit selection mode if no items are selected
            if (next.size === 0 && isSelectionMode) {
                setIsSelectionMode(false);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        const allIds = conversations.map((c) => c.conversation_id);
        const allSelected = allIds.length > 0 && allIds.every((id) => selectedConversations.has(id));

        if (allSelected) {
            // Deselect all
            setSelectedConversations(new Set());
            setIsSelectionMode(false);
        } else {
            // Select all visible conversations
            setSelectedConversations(new Set(allIds));
            if (!isSelectionMode) {
                setIsSelectionMode(true);
            }
        }
    };

    const handleEnterSelectionMode = (id: string) => {
        setIsSelectionMode(true);
        setSelectedConversations(new Set([id]));
    };

    const handleBulkDeleteClick = () => {
        const count = selectedConversations.size;
        if (count === 0) return;
        setShowBulkDeleteModal(true);
    };

    const handleBulkDeleteConfirm = () => {
        bulkDeleteConversations.mutate(Array.from(selectedConversations), {
            onSuccess: () => {
                setSelectedConversations(new Set());
                setIsSelectionMode(false);
                setShowBulkDeleteModal(false);
                refetch();
            },
        });
    };

    const handleSingleDeleteConfirm = () => {
        if (!conversationToDelete) return;
        deleteConversation.mutate(conversationToDelete, {
            onSuccess: () => {
                setSelectedConversations((prev) => {
                    const next = new Set(prev);
                    next.delete(conversationToDelete);
                    return next;
                });
                setShowSingleDeleteModal(false);
                setConversationToDelete(null);
                refetch();
            },
        });
    };

    const handleRename = (id: string) => {
        const conversation = conversations.find((c) => c.conversation_id === id);
        setEditingConversationId(id);
        // Assicurati che il valore sia sempre una stringa
        setEditingTitle(String(conversation?.title || ""));
    };

    const handleSaveRename = (id: string, title: string) => {
        updateConversation.mutate(
            { conversationId: id, data: { title: title || null } },
            {
                onSuccess: () => {
                    setEditingConversationId(null);
                    setEditingTitle("");
                    refetch();
                },
            }
        );
    };

    const handleCancelRename = () => {
        setEditingConversationId(null);
        setEditingTitle("");
    };

    const handleToggleStar = (id: string, starred: boolean) => {
        toggleStarConversation.mutate(
            { conversationId: id, starred },
            {
                onSuccess: () => {
                    refetch();
                },
            }
        );
    };

    const handleDelete = (id: string) => {
        setConversationToDelete(id);
        setShowSingleDeleteModal(true);
    };

    return (
        <>
            <SlideoutMenu
                isOpen={isOpen}
                onOpenChange={(open) => (!open ? onClose() : undefined)}
                className="ml-auto w-screen md:w-[33vw] max-w-none pl-0 md:pl-0 z-1000"
                dialogClassName="bg-primary_alt ring-secondary_alt shadow-xl w-full pl-0"
            >
                <SlideoutMenu.Content role="dialog" aria-label={t("aiChat.chatHistory")} className="flex flex-col h-full p-0">
                    <SlideoutMenu.Header onClose={onClose} className="flex-shrink-0">
                        <div className="w-full pr-12">
                            <h3 className="text-lg font-medium text-brand-primary mb-2">{t("aiChat.chatHistory")}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                {!isSelectionMode ? (
                                    <>
                                        <Button
                                            onClick={() => {
                                                createNewChat();
                                                onClose();
                                            }}
                                            color="link-color"
                                            size="sm"
                                            iconLeading={Plus}
                                        >
                                            {t("aiChat.newChat")}
                                        </Button>
                                        {selectedConversations.size > 0 && (
                                            <Button
                                                onClick={() => setIsSelectionMode(true)}
                                                color="secondary"
                                                size="sm"
                                            >
                                                {t("aiChat.selectedCount", { count: selectedConversations.size })}
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex items-center justify-between gap-2 w-full">
                                        <div className="flex items-center min-w-0">
                                            <Checkbox
                                                isSelected={
                                                    conversations.length > 0 &&
                                                    conversations.every((c) => selectedConversations.has(c.conversation_id))
                                                }
                                                isIndeterminate={
                                                    conversations.length > 0 &&
                                                    conversations.some((c) => selectedConversations.has(c.conversation_id)) &&
                                                    !conversations.every((c) => selectedConversations.has(c.conversation_id))
                                                }
                                                onChange={handleSelectAll}
                                                label={t("aiChat.selectedCount", { count: selectedConversations.size })}
                                                aria-label={
                                                    conversations.length > 0 &&
                                                        conversations.every((c) => selectedConversations.has(c.conversation_id))
                                                        ? t("aiChat.deselectAll")
                                                        : t("aiChat.selectAll")
                                                }
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Tooltip title={t("aiChat.bulkDelete")} placement="top">
                                                <Button
                                                    onClick={handleBulkDeleteClick}
                                                    color="primary-destructive"
                                                    size="sm"
                                                    iconLeading={Trash01}
                                                    isDisabled={selectedConversations.size === 0}
                                                    aria-label={t("aiChat.bulkDelete")}
                                                />
                                            </Tooltip>
                                            <Tooltip title={t("aiChat.cancelEdit")} placement="top">
                                                <Button
                                                    onClick={() => {
                                                        setIsSelectionMode(false);
                                                        setSelectedConversations(new Set());
                                                    }}
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={X}
                                                    aria-label={t("aiChat.cancelEdit")}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </SlideoutMenu.Header>
                    <div className="flex flex-1 min-h-0 overflow-hidden w-full">
                        <div
                            ref={scrollContainerRef}
                            className="flex-1 overflow-y-auto p-4 md:p-6"
                            onScroll={handleScroll}
                        >
                            {isLoading ? (
                                <div className="text-sm text-tertiary">{t("common.loading", { defaultValue: "Loading..." })}</div>
                            ) : conversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center">
                                    <p className="text-sm text-tertiary">{t("aiChat.noChats")}</p>
                                </div>
                            ) : (
                                <div className="space-y-2 w-full">
                                    {conversations.map((conversation) => {
                                        const isActive = chatTabs.some(
                                            (tab) => tab.backendConversationId === conversation.conversation_id
                                        );
                                        const isSelected = selectedConversations.has(conversation.conversation_id);

                                        return (
                                            <ChatItem
                                                key={conversation.conversation_id}
                                                conversation={conversation}
                                                isActive={isActive}
                                                isSelected={isSelected}
                                                isSelectionMode={isSelectionMode}
                                                editingConversationId={editingConversationId}
                                                editingTitle={editingTitle}
                                                onSelect={handleSelect}
                                                onOpen={(id, title) => {
                                                    openConversation(id, title);
                                                    onClose();
                                                }}
                                                onRename={handleRename}
                                                onSaveRename={handleSaveRename}
                                                onCancelRename={handleCancelRename}
                                                onSetEditingTitle={setEditingTitle}
                                                onToggleStar={handleToggleStar}
                                                onDelete={handleDelete}
                                                onEnterSelectionMode={handleEnterSelectionMode}
                                            />
                                        );
                                    })}
                                    {isFetchingNextPage && (
                                        <div className="text-sm text-tertiary text-center py-4">
                                            {t("common.loading", { defaultValue: "Loading..." })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </SlideoutMenu.Content>
            </SlideoutMenu>

            {/* Bulk Delete Confirmation Modal */}
            <ModalOverlay isOpen={showBulkDeleteModal} onOpenChange={(open) => !open && setShowBulkDeleteModal(false)}>
                <Modal className="max-w-md">
                    <Dialog>
                        <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                            <div className="mb-6 flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-primary">{t("aiChat.bulkDelete")}</h2>
                                <Button
                                    onClick={() => setShowBulkDeleteModal(false)}
                                    color="tertiary"
                                    size="sm"
                                    iconLeading={X}
                                    aria-label="Close"
                                />
                            </div>

                            <div className="mb-4 flex justify-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-secondary">
                                    <AlertTriangle className="h-6 w-6 text-fg-error-secondary" />
                                </div>
                            </div>

                            <p className="mb-6 text-center text-sm text-tertiary">
                                {t("aiChat.confirmBulkDelete", { count: selectedConversations.size })}
                            </p>

                            <div className="flex justify-end gap-3">
                                <Button
                                    onClick={() => setShowBulkDeleteModal(false)}
                                    isDisabled={bulkDeleteConversations.isPending}
                                    color="secondary"
                                    size="sm"
                                >
                                    {t("aiChat.cancelEdit")}
                                </Button>
                                <Button
                                    onClick={handleBulkDeleteConfirm}
                                    isDisabled={bulkDeleteConversations.isPending}
                                    isLoading={bulkDeleteConversations.isPending}
                                    color="primary-destructive"
                                    size="sm"
                                    iconLeading={!bulkDeleteConversations.isPending ? Trash01 : undefined}
                                >
                                    {t("aiChat.delete")}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Single Delete Confirmation Modal */}
            <ModalOverlay isOpen={showSingleDeleteModal} onOpenChange={(open) => !open && setShowSingleDeleteModal(false)}>
                <Modal className="max-w-md">
                    <Dialog>
                        <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                            <div className="mb-6 flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-primary">{t("aiChat.deleteChat")}</h2>
                                <Button
                                    onClick={() => {
                                        setShowSingleDeleteModal(false);
                                        setConversationToDelete(null);
                                    }}
                                    color="tertiary"
                                    size="sm"
                                    iconLeading={X}
                                    aria-label="Close"
                                />
                            </div>

                            <div className="mb-4 flex justify-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-secondary">
                                    <AlertTriangle className="h-6 w-6 text-fg-error-secondary" />
                                </div>
                            </div>

                            <p className="mb-6 text-center text-sm text-tertiary">{t("aiChat.confirmDelete")}</p>

                            <div className="flex justify-end gap-3">
                                <Button
                                    onClick={() => {
                                        setShowSingleDeleteModal(false);
                                        setConversationToDelete(null);
                                    }}
                                    isDisabled={deleteConversation.isPending}
                                    color="secondary"
                                    size="sm"
                                >
                                    {t("aiChat.cancelEdit")}
                                </Button>
                                <Button
                                    onClick={handleSingleDeleteConfirm}
                                    isDisabled={deleteConversation.isPending}
                                    isLoading={deleteConversation.isPending}
                                    color="primary-destructive"
                                    size="sm"
                                    iconLeading={!deleteConversation.isPending ? Trash01 : undefined}
                                >
                                    {t("aiChat.delete")}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </>
    );
};
